import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import TaskSkeleton from './TaskSkeleton';
import Select from 'react-select';

export default function AgentStudentList() {
    const { http, user } = AuthUser();
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [classes, setClasses] = useState([]);
    
    // Filter states
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Debounce timer for search
    const [searchTimer, setSearchTimer] = useState(null);
    
    // Tooltip state for hold remarks
    const [visibleTooltip, setVisibleTooltip] = useState(null);

    // Select options
    const agentOptions = useMemo(() => users.map(u => ({
        value: u.id,
        label: `${u.name} ${u.last_name || ''}`.trim()
    })), [users]);

    const classOptions = useMemo(() => classes.map(c => ({
        value: c.class_name,
        label: c.class_name
    })), [classes]);

    const statusOptions = [
        { value: 1, label: 'Approved' },
        { value: 0, label: 'Not Approved' },
        { value: 2, label: 'Cancelled' },
        { value: 3, label: 'Hold' }
    ];

    const typeOptions = [
        { value: 0, label: 'Open Seat' },
        { value: 1, label: 'Consult' }
    ];

    // Custom styles for react-select
    const selectStyles = {
        control: (base) => ({ ...base, minHeight: '31px', fontSize: '13px' }),
        valueContainer: (base) => ({ ...base, padding: '0 6px' }),
        input: (base) => ({ ...base, margin: '0', padding: '0' }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
        menu: (base) => ({ ...base, zIndex: 9999, fontSize: '13px' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        fetchAgentStudents();
        fetchUsers();
        fetchClasses();
    }, []);
    
    // Apply filters whenever filter states change
    useEffect(() => {
        applyFilters();
    }, [students, selectedClass, selectedAgent, selectedStatus, selectedType, searchTerm]);

    const fetchAgentStudents = () => {
        setIsLoading(true);
        http.get(`/agent_students?company_id=${user.company_id}`)
            .then((res) => {
                setIsLoading(false);
                if (res.data.success && Array.isArray(res.data.data)) {
                    setStudents(res.data.data);
                    setFilteredStudents(res.data.data); // Initialize filtered students with all students
                } else {
                    console.error("API response is not as expected:", res.data);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching agent students:", error);
                toast.error("Failed to load agent students");
            });
    };
    
    const fetchClasses = () => {
        http.get(`/class?company_id=${user.company_id}`)
            .then((res) => {
                if (Array.isArray(res.data.data)) {
                    setClasses(res.data.data);
                } else {
                    console.error("API response for classes is not an array");
                }
            })
            .catch((error) => {
                console.error("Error fetching classes:", error);
            });
    };

    const fetchUsers = () => {
        http.get(`/users_by_company_department?company_id=${user.company_id}&department_id=4`)
            .then((res) => {
                if (res.data.success && Array.isArray(res.data.users)) {
                    setUsers(res.data.users);
                } else {
                    console.error("API response for users is not as expected:", res.data);
                }
            })
            .catch((error) => {
                console.error("Error fetching users:", error);
            });
    };

    const getUserName = (userId) => {
        const user = users.find(u => u.id === userId);
        return user ? `${user.name} ${user.last_name || ''}` : 'Unknown';
    };
    
    // Apply all filters to the students array
    const applyFilters = () => {
        let result = [...students];
        
        // Apply class filter
        if (selectedClass) {
            result = result.filter(student => student.class === selectedClass.value);
        }
        
        // Apply agent filter
        if (selectedAgent) {
            result = result.filter(student => student.user_id === selectedAgent.value);
        }
        
        // Apply status filter
        if (selectedStatus !== null) {
            result = result.filter(student => student.status === selectedStatus.value);
        }
        
        // Apply type filter
        if (selectedType !== null) {
            result = result.filter(student => student.type === selectedType.value);
        }
        
        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(student => 
                (student.student_name && student.student_name.toLowerCase().includes(term)) ||
                (student.mobile_number && student.mobile_number.toLowerCase().includes(term)) ||
                (student.email_address && student.email_address.toLowerCase().includes(term))
            );
        }
        
        setFilteredStudents(result);
    };
    
    // Handle search with debounce
    const handleSearch = (e) => {
        const value = e.target.value;
        
        // Clear previous timer
        if (searchTimer) {
            clearTimeout(searchTimer);
        }
        
        // Set a new timer
        const timer = setTimeout(() => {
            setSearchTerm(value);
        }, 300); // 300ms debounce
        
        setSearchTimer(timer);
    };
    
    // Reset all filters
    const resetFilters = () => {
        setSelectedClass(null);
        setSelectedAgent(null);
        setSelectedStatus(null);
        setSelectedType(null);
        setSearchTerm('');
        document.getElementById('search-input').value = '';
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this student?')) {
            setIsLoading(true);
            http.delete(`/agent_student_delete/${id}`)
                .then((res) => {
                    setIsLoading(false);
                    if (res.data.success) {
                        toast.success("Student deleted successfully");
                        fetchAgentStudents(); // Refresh the list
                    } else {
                        toast.error(res.data.message || "Failed to delete student");
                    }
                })
                .catch((error) => {
                    setIsLoading(false);
                    console.error("Error deleting agent student:", error);
                    toast.error("An error occurred while deleting the student");
                });
        }
    };
    
    const handleApprove = (id, status) => {
        // Get action name based on status
        let action;
        switch (status) {
            case 1:
                action = 'approve';
                break;
            case 0:
                action = 'mark as not approved';
                break;
            case 2:
                action = 'cancel';
                break;
            case 3:
                action = 'put on hold';
                break;
            default:
                action = 'update';
        }
        
        if (window.confirm(`Are you sure you want to ${action} this student?`)) {
            setIsLoading(true);
            http.put(`/agent_student_approve/${id}`, { status })
                .then((res) => {
                    setIsLoading(false);
                    if (res.data.success) {
                        toast.success(res.data.message || `Student ${action}d successfully`);
                        fetchAgentStudents(); // Refresh the list
                    } else {
                        toast.error(res.data.message || `Failed to ${action} student`);
                    }
                })
                .catch((error) => {
                    setIsLoading(false);
                    console.error(`Error ${action}ing agent student:`, error);
                    toast.error(`An error occurred while ${action}ing the student`);
                });
        }
    };

    return (
        <>
            <style>
                {`
                    .btn-xs {
                        padding: 0.1rem 0.3rem !important;
                        font-size: 0.675rem !important;
                        line-height: 1.2 !important;
                        border-radius: 0.2rem !important;
                    }
                    .btn-group-xs > .btn {
                        padding: 0.1rem 0.3rem !important;
                        font-size: 0.675rem !important;
                        line-height: 1.2 !important;
                        border-radius: 0.2rem !important;
                    }
                    .action-cell {
                        white-space: nowrap;
                        width: 1%;
                    }
                    .hold-tooltip-container {
                        position: relative;
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                    }
                    .hold-info-icon {
                        cursor: pointer;
                        color: #0dcaf0;
                        font-size: 14px;
                    }
                    .hold-info-icon:hover {
                        color: #0aa2c0;
                    }
                    .hold-tooltip {
                        position: absolute;
                        bottom: 100%;
                        left: 50%;
                        transform: translateX(-50%);
                        background-color: #333;
                        color: #fff;
                        padding: 6px 10px;
                        border-radius: 4px;
                        font-size: 12px;
                        white-space: nowrap;
                        z-index: 1000;
                        margin-bottom: 5px;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                        max-width: 250px;
                        white-space: normal;
                        text-align: center;
                    }
                    .hold-tooltip::after {
                        content: '';
                        position: absolute;
                        top: 100%;
                        left: 50%;
                        transform: translateX(-50%);
                        border-width: 5px;
                        border-style: solid;
                        border-color: #333 transparent transparent transparent;
                    }
                `}
            </style>
            <div className="container-fluid px-3 mt-2">
                <Toaster />
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Conslt Students</h5>
                    <Link to="/agent_student_add" className="btn btn-primary btn-sm">
                        <i className="fa fa-plus me-1"></i>Add Student
                    </Link>
                </div>
                
                {/* Filter and Search Section - All in one compact row */}
                <div className="card mb-2 border-0 shadow-sm" style={{ background: '#f8f9fa' }}>
                    <div className="card-body py-2 px-3">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {/* Search */}
                            <div className="input-group input-group-sm" style={{ width: '180px' }}>
                                <span className="input-group-text bg-white"><i className="fa fa-search"></i></span>
                                <input 
                                    type="text" 
                                    id="search-input"
                                    className="form-control bg-white" 
                                    placeholder="Search..." 
                                    onChange={handleSearch}
                                />
                            </div>
                            
                            {/* Consult Filter */}
                            <div style={{ width: '160px' }}>
                                <Select
                                    options={agentOptions}
                                    value={selectedAgent}
                                    onChange={setSelectedAgent}
                                    placeholder="Select Consult..."
                                    isClearable
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            
                            {/* Status Filter */}
                            <div style={{ width: '140px' }}>
                                <Select
                                    options={statusOptions}
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                    placeholder="Select Status..."
                                    isClearable
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            
                            {/* Type Filter */}
                            <div style={{ width: '130px' }}>
                                <Select
                                    options={typeOptions}
                                    value={selectedType}
                                    onChange={setSelectedType}
                                    placeholder="Select Type..."
                                    isClearable
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            
                            {/* Class Filter */}
                            <div style={{ width: '120px' }}>
                                <Select
                                    options={classOptions}
                                    value={selectedClass}
                                    onChange={setSelectedClass}
                                    placeholder="Class"
                                    isClearable
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            
                            {/* Reset Button */}
                            <button 
                                className="btn btn-secondary btn-sm" 
                                onClick={resetFilters}
                                title="Reset Filters"
                            >
                                <i className="fa fa-undo me-1"></i>Reset
                            </button>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <TaskSkeleton />
                ) : (
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>Student Name</th>
                                    <th>Class</th>
                                    <th>Mobile Number</th>
                                    <th>Email Address</th>
                                    <th>Father Name</th>
                                    <th>Conslt</th>
                                    <th>Status</th>
                                    <th>Type</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.length > 0 ? (
                                    filteredStudents.map((student, index) => (
                                        <tr key={student.id}>
                                            <td>{index + 1}</td>
                                            <td>{student.student_name}</td>
                                            <td>{student.class || '-'}</td>
                                            <td>{student.mobile_number || '-'}</td>
                                            <td>{student.email_address || '-'}</td>
                                            <td>{student.father_name || '-'}</td>
                                            <td>{getUserName(student.user_id)}</td>
                                            <td>
                                                {student.status === 1 ? (
                                                    <span className="badge bg-success">Approved</span>
                                                ) : student.status === 0 ? (
                                                    <span className="badge bg-warning text-dark">Not Approved</span>
                                                ) : student.status === 2 ? (
                                                    <span className="badge bg-danger">Cancelled</span>
                                                ) : student.status === 3 ? (
                                                    <div className="hold-tooltip-container">
                                                        <span className="badge bg-info">Hold</span>
                                                        {student.hold_remark && (
                                                            <>
                                                                <i 
                                                                    className="fa fa-info-circle hold-info-icon"
                                                                    onMouseEnter={() => setVisibleTooltip(student.id)}
                                                                    onMouseLeave={() => setVisibleTooltip(null)}
                                                                    onClick={() => setVisibleTooltip(visibleTooltip === student.id ? null : student.id)}
                                                                ></i>
                                                                {visibleTooltip === student.id && (
                                                                    <div className="hold-tooltip">
                                                                        {student.hold_remark}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="badge bg-secondary">Pending</span>
                                                )}
                                            </td>
                                            <td>
                                                {student.type === 1 ? (
                                                    <span className="badge bg-primary">Consult</span>
                                                ) : student.type === 0 ? (
                                                    <span className="badge bg-secondary">Open Seat</span>
                                                ) : (
                                                    <span className="badge bg-light text-dark">-</span>
                                                )}
                                            </td>
                                            <td className="action-cell">
                                                <div className="d-flex">
                                                    <Link to={`/agent_student_edit/${student.id}`} className="btn btn-info btn-xs px-1 py-0 me-1" title="Edit">
                                                        <i className="fa fa-edit fa-xs"></i>
                                                    </Link>
                                                    <div className="dropdown d-inline-block me-1">
                                                        {/* <button 
                                                            className={`btn ${student.status === 1 ? 'btn-success' : student.status === 0 ? 'btn-warning' : student.status === 2 ? 'btn-danger' : 'btn-info'} btn-xs px-1 py-0 dropdown-toggle`}
                                                            type="button" 
                                                            id={`statusDropdown-${student.id}`} 
                                                            data-bs-toggle="dropdown" 
                                                            aria-expanded="false"
                                                            title="Change Status"
                                                        >
                                                            <i className="fa fa-exchange-alt fa-xs"></i>
                                                        </button> */}
                                                        <ul className="dropdown-menu dropdown-menu-sm" aria-labelledby={`statusDropdown-${student.id}`}>
                                                            <li><button className="dropdown-item" onClick={() => handleApprove(student.id, 1)}>Approve</button></li>
                                                            <li><button className="dropdown-item" onClick={() => handleApprove(student.id, 0)}>Not Approve</button></li>
                                                            <li><button className="dropdown-item" onClick={() => handleApprove(student.id, 2)}>Cancel</button></li>
                                                            <li><button className="dropdown-item" onClick={() => handleApprove(student.id, 3)}>Hold</button></li>
                                                        </ul>
                                                    </div>
                                                    {/* <button 
                                                        className="btn btn-danger btn-xs px-1 py-0" 
                                                        onClick={() => handleDelete(student.id)}
                                                        title="Delete"
                                                    >
                                                        <i className="fa fa-trash fa-xs"></i>
                                                    </button> */}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="text-center">
                                            {students.length > 0 ? 'No students match the current filters' : 'No agent students found'}
                                        </td>
                                    </tr>
                                )}
                                {filteredStudents.length > 0 && (
                                    <tr className="table-light">
                                        <td colSpan="10" className="text-end">
                                            <small>
                                                Showing {filteredStudents.length} of {students.length} students
                                                {(selectedClass || selectedAgent || selectedStatus || selectedType || searchTerm) && (
                                                    <button 
                                                        className="btn btn-link btn-sm py-0 ms-2" 
                                                        onClick={resetFilters}
                                                    >
                                                        Clear filters
                                                    </button>
                                                )}
                                            </small>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}
